<script setup lang="ts">
import api from "@/api";
import { getLocalStorageReac } from "@/assets/js/helpers";
import { useDefaultStore } from "@/store";
import { useWebSocketStore } from "@/store/websocket";

definePage({
	name: "PeopleWeakestLinkPage",
});

type TRouteParams = {
	id: string;
};

const route = useRoute();
const store = useDefaultStore();
const wsStore = useWebSocketStore();
const isLoading = ref(true);
const hasSeenActiveGameRoom = ref(false);
const isFinalResultsVisible = ref(false);
const isRequestingPlayAgain = ref(false);
let finalResultsTimeout: ReturnType<typeof setTimeout> | null = null;

const sortedGameResults = computed(() =>
	[...store.multiplayerGameResults].sort((a, b) => {
		if (a.rank !== b.rank) {
			return a.rank - b.rank;
		}

		return a.mpg_usr_order - b.mpg_usr_order;
	}),
);

const shouldShowGameRoom = computed(
	() =>
		store.multiplayerGameActive ||
		(store.multiplayerGameIsOver &&
			hasSeenActiveGameRoom.value &&
			!isFinalResultsVisible.value),
);

const isCurrentPlayerCreator = computed(() =>
	store.multiplayerGameResults.some(
		(player) => player.is_me && player.mpg_usr_is_creator === 1,
	),
);

function clearFinalResultsTimeout() {
	if (finalResultsTimeout) {
		clearTimeout(finalResultsTimeout);
		finalResultsTimeout = null;
	}
}

async function initGame() {
	await store.waitForAppReady();

	store.multiplayerGameCategory = store.MAIN_CATEGORIES.PEOPLE;
	store.gameToken = (route.params as TRouteParams).id;
	store.playerToken =
		store.playerToken || getLocalStorageReac("user-token", false, "").value || "";

	await wsStore.safeSend({
		type: wsStore.MESSAGE_TYPES.PLAYER_JOIN_ROOM,
		data: {
			plr_token: store.playerToken,
			mpg_token: store.gameToken,
		},
	});
}

async function requestPlayAgain() {
	if (
		isRequestingPlayAgain.value ||
		!store.gameToken ||
		!store.playerToken
	) {
		return;
	}

	isRequestingPlayAgain.value = true;

	try {
		await api.postMultiplayerPlayAgain({
			mpg_token: store.gameToken,
			token: store.playerToken,
		});
	} finally {
		isRequestingPlayAgain.value = false;
	}
}

watchEffect(() => {
	if (
		store.isVisibleJoinDialog ||
		store.isVisibleLobbyModal ||
		store.multiplayerGameActive ||
		store.multiplayerGameIsOver
	) {
		isLoading.value = false;
	}
});

watch(
	() => store.multiplayerGameActive,
	(isActive) => {
		if (isActive) {
			hasSeenActiveGameRoom.value = true;
			isFinalResultsVisible.value = false;
			clearFinalResultsTimeout();
		}
	},
);

watch(
	() => store.multiplayerGameIsOver,
	(isOver) => {
		clearFinalResultsTimeout();

		if (!isOver) {
			isFinalResultsVisible.value = false;
			return;
		}

		if (!hasSeenActiveGameRoom.value) {
			isFinalResultsVisible.value = true;
			return;
		}

		isFinalResultsVisible.value = false;
		finalResultsTimeout = setTimeout(() => {
			isFinalResultsVisible.value = true;
		}, 1250);
	},
);

onMounted(() => {
	void initGame();
});

onBeforeUnmount(() => {
	clearFinalResultsTimeout();
	if (store.gameToken === (route.params as TRouteParams).id) {
		store.gameToken = "";
	}
});
</script>

<template>
	<div class="multiplayer-page">
		<div
			v-if="isLoading"
			class="progress-spinner-wrapper w-full h-full flex justify-center items-center"
		>
			<ProgressSpinner
				style="width: 50px; height: 50px"
				stroke-width="8"
				fill="transparent"
				animation-duration=".5s"
			/>
		</div>

		<JoinDialog />
		<LobbyModal />
		<GameRoom v-if="shouldShowGameRoom" />

		<section
			v-if="isFinalResultsVisible"
			class="game-over"
		>
			<button
				class="category-back-link"
				type="button"
				@click="$router.push('/people')"
			>
				← Back to People
			</button>

			<div class="game-over__inner">
				<header class="game-over__header">
					<p class="game-over__eyebrow">Game over</p>
					<h1 class="game-over__title">Final results</h1>
					<div class="game-over__actions">
						<button
							v-if="isCurrentPlayerCreator"
							type="button"
							class="play-again-results-button"
							:disabled="isRequestingPlayAgain"
							:aria-label="
								isRequestingPlayAgain ? 'Starting new game' : 'Play again'
							"
							:title="isRequestingPlayAgain ? 'Starting...' : 'Play again'"
							@click="requestPlayAgain"
						>
							<i-fa-rotate-right
								class="play-again-results-button__icon"
								aria-hidden="true"
							/>
						</button>
						<FinalResultsImageGenerator
							category-label="People"
							:players="sortedGameResults"
						/>
					</div>
				</header>

				<div class="results-list">
					<div
						v-for="(player, index) in sortedGameResults"
						:key="player.mpg_usr_order"
						class="result-row"
						:class="{
							'is-me': player.is_me,
							'is-winner': player.is_winner,
						}"
						:style="{ animationDelay: `${110 + index * 55}ms` }"
					>
						<div class="result-row__rank">#{{ player.rank }}</div>
						<div
							class="result-row__color"
							:style="{ backgroundColor: player.mpg_usr_color_hex ?? '#fff' }"
						></div>
						<div class="result-row__player">
							<span class="result-row__name">
								{{ player.usr_username || "Guest" }}
							</span>
							<span
								v-if="player.is_me"
								class="result-row__tag"
							>
								You
							</span>
							<span
								v-if="player.is_winner"
								class="result-row__tag result-row__tag--winner"
							>
								Winner
							</span>
						</div>
						<div class="result-row__round">
							Round {{ player.reached_round }}
						</div>
					</div>
				</div>
			</div>
		</section>
	</div>
</template>

<style scoped lang="scss">
.multiplayer-page {
	min-height: 100dvh;
	background: rgb(17, 17, 17);
	color: rgb(255, 255, 255);
}

.progress-spinner-wrapper {
	min-height: 100dvh;
}

.game-over {
	position: relative;
	height: 100dvh;
	min-height: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 72px 16px calc(24px + env(safe-area-inset-bottom));
	background: linear-gradient(rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0.72)),
		radial-gradient(
			circle at top left,
			rgba(52, 144, 220, 0.28),
			transparent 34%
		),
		rgb(17, 17, 17);
	overflow: hidden;
}

.category-back-link {
	position: fixed;
	top: 18px;
	left: 18px;
	border: 0;
	background: transparent;
	color: #fff;
	font-weight: 800;
	cursor: pointer;
}

.game-over__inner {
	width: min(680px, 100%);
	max-height: 100%;
	min-height: 0;
	display: flex;
	flex-direction: column;
	animation: final-results-panel-in 520ms cubic-bezier(0.2, 0.85, 0.2, 1)
		both;
}

.game-over__header {
	flex: 0 0 auto;
	margin-bottom: 22px;
	text-align: center;
	animation: final-results-title-in 560ms cubic-bezier(0.2, 0.85, 0.2, 1)
		80ms both;
}

.game-over__eyebrow {
	margin: 0 0 8px;
	color: rgba(255, 255, 255, 0.62);
	font-size: 12px;
	font-weight: 700;
	text-transform: uppercase;
}

.game-over__title {
	margin: 0;
	font-size: clamp(34px, 7vw, 68px);
	font-weight: 900;
	line-height: 1;
}

.game-over__actions {
	margin-top: 18px;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
}

.play-again-results-button {
	width: 42px;
	height: 42px;
	padding: 0;
	border: 1px solid rgba(255, 255, 255, 0.14);
	border-radius: 50%;
	background: rgba(255, 255, 255, 0.14);
	color: rgb(255, 255, 255);
	cursor: pointer;
	backdrop-filter: blur(10px);
	box-shadow: 0 12px 30px rgba(0, 0, 0, 0.18);
	display: inline-flex;
	align-items: center;
	justify-content: center;
	transition:
		background 0.2s ease,
		border-color 0.2s ease,
		transform 0.2s ease,
		opacity 0.2s ease;
}

.play-again-results-button:disabled {
	cursor: not-allowed;
	opacity: 0.65;
}

.play-again-results-button:not(:disabled):hover {
	background: rgba(255, 255, 255, 0.2);
	border-color: rgba(255, 255, 255, 0.24);
	transform: translateY(-1px);
}

.play-again-results-button__icon {
	width: 20px;
	height: 20px;
	display: block;
}

.results-list {
	min-height: 0;
	overflow-y: auto;
	overflow-x: hidden;
	padding-right: 4px;
	display: flex;
	flex-direction: column;
	gap: 10px;
	-webkit-overflow-scrolling: touch;
}

.result-row {
	display: grid;
	grid-template-columns: auto auto minmax(0, 1fr) auto;
	align-items: center;
	gap: 12px;
	padding: 14px 16px;
	border: 1px solid rgba(255, 255, 255, 0.1);
	border-radius: 8px;
	background: rgba(255, 255, 255, 0.08);
	backdrop-filter: blur(10px);
	opacity: 0;
	animation: final-results-row-in 420ms cubic-bezier(0.2, 0.85, 0.2, 1)
		both;
}

.result-row.is-me {
	border-color: rgba(255, 255, 255, 0.34);
	background: rgba(255, 255, 255, 0.13);
}

.result-row.is-winner {
	box-shadow: inset 3px 0 0 rgb(245, 196, 81);
}

.result-row__rank {
	min-width: 40px;
	font-size: 18px;
	font-weight: 900;
}

.result-row__color {
	width: 14px;
	height: 14px;
	border-radius: 50%;
	box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.16);
}

.result-row__player {
	min-width: 0;
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}

.result-row__name {
	min-width: 0;
	overflow: hidden;
	font-weight: 800;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.result-row__tag {
	border-radius: 999px;
	padding: 3px 7px;
	background: rgba(255, 255, 255, 0.14);
	color: rgba(255, 255, 255, 0.82);
	font-size: 11px;
	font-weight: 800;
}

.result-row__tag--winner {
	background: rgba(245, 196, 81, 0.2);
	color: rgb(255, 226, 153);
}

.result-row__round {
	color: rgba(255, 255, 255, 0.7);
	font-size: 13px;
	font-weight: 700;
	white-space: nowrap;
}

@keyframes final-results-panel-in {
	0% {
		opacity: 0;
		transform: translateY(18px) scale(0.98);
	}

	100% {
		opacity: 1;
		transform: translateY(0) scale(1);
	}
}

@keyframes final-results-title-in {
	0% {
		opacity: 0;
		transform: translateY(10px);
	}

	100% {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes final-results-row-in {
	0% {
		opacity: 0;
		transform: translateY(12px);
	}

	100% {
		opacity: 1;
		transform: translateY(0);
	}
}

@media (max-width: 520px) {
	.result-row {
		grid-template-columns: auto auto minmax(0, 1fr);
	}

	.result-row__round {
		grid-column: 3;
	}
}

@media (min-width: 641px) {
	.game-over {
		padding: 32px 16px;
	}
}
</style>
